import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Autocomplete,
  Grid,
  Divider,
  CircularProgress,
  Alert,
  Breadcrumbs,
  alpha,
  useTheme,
  Chip
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Paid as PaidIcon,
  Add as AddIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  MonetizationOn as MonetizationOnIcon,
  BookmarkBorder as BookmarkBorderIcon
} from '@mui/icons-material';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import apiService from '../../../services/apiService';
import { getVoucherTemplates, VoucherTemplate } from '../../../services/financeService';
import axios from 'axios'; // إضافة استيراد axios للتحقق من أنواع الأخطاء
import moment from 'moment-timezone';

// واجهة الحساب للقائمة المنسدلة
interface AccountOption {
  id: string;
  accountNumber: string;
  name: string;
  label: string;
  type?: string;
  isActive?: boolean;
}

// مكون لعرض حالة اتصال الخادم
interface ServerStatusProps {
  onCheckConnection: () => Promise<boolean>;
}

const ServerStatus: React.FC<ServerStatusProps> = ({ onCheckConnection }) => {
  const [status, setStatus] = useState<'checking' | 'online' | 'offline'>('checking');
  
  const checkStatus = async () => {
    setStatus('checking');
    const isConnected = await onCheckConnection();
    setStatus(isConnected ? 'online' : 'offline');
  };
  
  useEffect(() => {
    checkStatus();
    // إعادة التحقق كل 30 ثانية
    const interval = setInterval(checkStatus, 30000);
    return () => clearInterval(interval);
  }, []);
  
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
      <Typography variant="body2" sx={{ mr: 1 }}>حالة الخادم:</Typography>
      {status === 'checking' ? (
        <Chip
          size="small"
          label="جاري التحقق..."
          color="default"
          icon={<CircularProgress size={14} />}
        />
      ) : status === 'online' ? (
        <Chip
          size="small"
          label="متصل"
          color="success"
          icon={<CheckCircleOutlineIcon style={{ fontSize: 14 }} />}
        />
      ) : (
        <Chip
          size="small"
          label="غير متصل"
          color="error"
          onClick={checkStatus}
        />
      )}
    </Box>
  );
};
// واجهة بيانات سند القبض
interface ReceiptData {
  id?: string;
  receiptNumber: string;
  date: string;
  status: 'مؤكد' | 'تحت الموافقة' | 'مرفوض';
  debitAccountId: string;
  creditAccountId: string;
  amount: number;
  description: string;
}

// توليد رقم سند افتراضي يتوافق تمامًا مع تنسيق الخادم
const generateDefaultReceiptNumber = () => {
  const today = new Date();
  const year = today.getFullYear();
  // استخدام تنسيق متطابق تمامًا مع المثال: RCV-2025-00001
  const sequenceNumber = Math.floor(Math.random() * 10000).toString().padStart(5, '0');
  return `RCV-${year}-${sequenceNumber}`;
};

// نموذج سند قبض فارغ للإنشاء
const emptyReceipt: ReceiptData = {
  receiptNumber: generateDefaultReceiptNumber(),
  date: new Date().toISOString().split('T')[0], // YYYY-MM-DD format
  status: 'تحت الموافقة',
  debitAccountId: '',
  creditAccountId: '',
  amount: 0,
  description: ''
};

// تحويل حسابات API إلى تنسيق القائمة المنسدلة
const mapApiAccountsToOptions = (accounts: any[]): AccountOption[] => {
  return accounts.map(account => ({
    id: account.id,
    accountNumber: account.accountNumber,
    name: account.name,
    label: `${account.accountNumber} - ${account.name}`,
    type: account.type,
    isActive: account.isActive
  }));
};

const ReceiptForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const { activeBranch } = useAuth();
  const isEditMode = Boolean(id);
  
  // حالة النموذج
  const [formData, setFormData] = useState<ReceiptData>(emptyReceipt);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);
  const [accounts, setAccounts] = useState<AccountOption[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [templates, setTemplates] = useState<VoucherTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<VoucherTemplate | null>(null);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  
  // جلب الحسابات من API
  useEffect(() => {
    const fetchAccounts = async () => {
      setAccountsLoading(true);
      try {
        // استدعاء API لجلب جميع الحسابات النشطة
        const { apiWithContext } = await import('../../../services/api');
        const response = await apiWithContext.get('/finance/accounts/selection/active', 'receipt-voucher').then(res => res.data);
        
        if (response && Array.isArray(response) && response.length > 0) {
          console.log('تم جلب الحسابات بنجاح. عدد الحسابات:', response.length);
          const mappedAccounts = mapApiAccountsToOptions(response);
          setAccounts(mappedAccounts);
          
          // طباعة معرفات الحسابات للتصحيح
          console.log('معرفات الحسابات المتاحة:',
            mappedAccounts.map(acc => ({id: acc.id, name: acc.name, accountNumber: acc.accountNumber}))
          );
          
          // مسح أي رسائل خطأ متعلقة بالحسابات
          setErrors(prev => {
            const newErrors = { ...prev };
            // إزالة رسائل الخطأ المتعلقة بالحسابات إذا تم تحميلها بنجاح
            if (newErrors.submit && (
              newErrors.submit.includes('حسابات') ||
              newErrors.submit.includes('الخادم الخلفي')
            )) {
              delete newErrors.submit;
            }
            return newErrors;
          });
        } else {
          console.warn('لم يتم العثور على حسابات من API');
          // لا نضيف رسالة خطأ هنا لتجنب إزعاج المستخدم
        }
      } catch (error) {
        console.error('خطأ في جلب الحسابات:', error);
        // لا نضيف رسالة خطأ هنا لتجنب إزعاج المستخدم
      } finally {
        setAccountsLoading(false);
      }
    };
    
    fetchAccounts();
  }, []);
  
  // جلب القوالب المحفوظة
  useEffect(() => {
    const fetchTemplates = async () => {
      if (isEditMode) return; // لا نحتاج القوالب في وضع التعديل
      
      setTemplatesLoading(true);
      try {
        const templatesData = await getVoucherTemplates({ templateType: 'receipt', isActive: true });
        setTemplates(templatesData);
      } catch (error) {
        console.error('خطأ في جلب القوالب:', error);
      } finally {
        setTemplatesLoading(false);
      }
    };
    
    fetchTemplates();
  }, [isEditMode]);
  
  // تطبيق القالب المختار
  const handleTemplateSelect = (template: VoucherTemplate | null) => {
    setSelectedTemplate(template);
    
    if (template) {
      // ملء الحسابات من القالب
      if (template.debitAccount) {
        setFormData(prev => ({
          ...prev,
          debitAccountId: template.debitAccount!.id
        }));
      }
      
      if (template.creditAccount) {
        setFormData(prev => ({
          ...prev,
          creditAccountId: template.creditAccount!.id
        }));
      }
      
      // مسح أي أخطاء متعلقة بالحسابات
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.debitAccountId;
        delete newErrors.creditAccountId;
        return newErrors;
      });
    }
  };
  
  // جلب بيانات سند القبض عند التعديل
  useEffect(() => {
    if (isEditMode && id) {
      setLoading(true);
      
      // محاولة استرجاع البيانات من الخادم بشكل مباشر
      const fetchReceiptData = async () => {
        try {
          // التأكد من وجود حسابات قبل محاولة جلب البيانات
          if (accounts.length === 0) {
            console.log('جاري جلب بيانات سند القبض رغم عدم توفر الحسابات حاليًا');
          }
          
          // استدعاء API لجلب بيانات السند
          const response = await apiService.get<any>(`/finance/receipts/${id}`);
          console.log('بيانات السند المسترجعة كاملة:', response);
          
          // الاستجابة من الخادم تحتوي على الحقل data الذي يحتوي على بيانات السند
          const receiptData = response.data || response.receipt || response;
          console.log('بيانات السند المعالجة:', receiptData);
          
          if (receiptData && receiptData.id) {
            // تحديد معرفات الحسابات بشكل صحيح
            let debitAccountId = '';
            let creditAccountId = '';
            
            // معالجة حقل debitAccount - طريقة متوافقة مع تنسيق الخادم
            if (receiptData.debitAccount) {
              if (typeof receiptData.debitAccount === 'object' && receiptData.debitAccount.id) {
                debitAccountId = receiptData.debitAccount.id;
                console.log('تم استخراج معرف الحساب المدين من الكائن:', debitAccountId);
              } else {
                debitAccountId = String(receiptData.debitAccount);
                console.log('تم استخراج معرف الحساب المدين كسلسلة:', debitAccountId);
              }
            }
            
            // معالجة حقل creditAccount - طريقة متوافقة مع تنسيق الخادم
            if (receiptData.creditAccount) {
              if (typeof receiptData.creditAccount === 'object' && receiptData.creditAccount.id) {
                creditAccountId = receiptData.creditAccount.id;
                console.log('تم استخراج معرف الحساب الدائن من الكائن:', creditAccountId);
              } else {
                creditAccountId = String(receiptData.creditAccount);
                console.log('تم استخراج معرف الحساب الدائن كسلسلة:', creditAccountId);
              }
            }
            
            // طباعة البيانات للتصحيح
            console.log('بيانات السند المستخرجة للنموذج:', {
              id: receiptData.id,
              receiptNumber: receiptData.receiptNumber,
              date: receiptData.date,
              status: receiptData.status,
              debitAccountId: debitAccountId,
              creditAccountId: creditAccountId,
              amount: receiptData.amount,
              description: receiptData.description
            });
            
            // تعيين قيم النموذج
            setFormData({
              id: receiptData.id,
              receiptNumber: receiptData.receiptNumber || '',
              date: receiptData.date ? (typeof receiptData.date === 'string' ? receiptData.date.split('T')[0] : new Date(receiptData.date).toISOString().split('T')[0]) : '',
              status: receiptData.status || 'تحت الموافقة',
              debitAccountId: debitAccountId,
              creditAccountId: creditAccountId,
              amount: typeof receiptData.amount === 'number' ? receiptData.amount : 0,
              description: receiptData.description || ''
            });
            
            // نسجل فقط تحذيرًا في وحدة التحكم إذا كانت معرفات الحسابات غير موجودة في قائمة الحسابات المتاحة
            // ولكن لا نعرض رسائل خطأ للمستخدم لأن النظام يعمل بشكل صحيح رغم ذلك
            const debitAccountExists = accounts.some(acc => acc.id === debitAccountId);
            const creditAccountExists = accounts.some(acc => acc.id === creditAccountId);
            
            if (!debitAccountExists || !creditAccountExists) {
              console.warn('أحد الحسابات أو كلاهما غير موجود في قائمة الحسابات المتاحة، ولكن سيتم استخدامه على أي حال:',
                { debitAccountExists, creditAccountExists, debitAccountId, creditAccountId });
            }
          } else {
            console.error('بيانات السند المسترجعة غير صالحة:', receiptData);
            throw new Error('لم يتم استرجاع بيانات صالحة للسند');
          }
        } catch (error) {
          console.error('خطأ في جلب بيانات سند القبض:', error);
          
          // عرض رسالة خطأ واضحة
          setErrors(prev => ({
            ...prev,
            submit: 'تعذر جلب بيانات سند القبض من الخادم. يمكنك المحاولة مرة أخرى أو العودة للقائمة.'
          }));
        } finally {
          setLoading(false);
        }
      };
      
      fetchReceiptData();
    }
  }, [id, isEditMode]); // إزالة accounts و navigate من التبعيات لمنع إعادة تنفيذ الدالة مرتين
  
  // معالجة تغيير حقول النص والأرقام
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // مسح رسالة الخطأ عند التغيير
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  
  // معالجة تغيير التاريخ
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    
    setFormData(prev => ({ ...prev, date: value }));
    
    // مسح رسالة الخطأ عند التغيير
    if (errors.date) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.date;
        return newErrors;
      });
    }
  };
  
  // معالجة تغيير الحساب المدين
  const handleDebitAccountChange = (event: React.SyntheticEvent, value: AccountOption | null) => {
    if (value) {
      setFormData(prev => ({ ...prev, debitAccountId: value.id }));
      
      // مسح رسالة الخطأ عند التغيير
      if (errors.debitAccountId) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.debitAccountId;
          return newErrors;
        });
      }
    } else {
      setFormData(prev => ({ ...prev, debitAccountId: '' }));
    }
  };
  
  // معالجة تغيير الحساب الدائن
  const handleCreditAccountChange = (event: React.SyntheticEvent, value: AccountOption | null) => {
    if (value) {
      setFormData(prev => ({ ...prev, creditAccountId: value.id }));
      
      // مسح رسالة الخطأ عند التغيير
      if (errors.creditAccountId) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.creditAccountId;
          return newErrors;
        });
      }
    } else {
      setFormData(prev => ({ ...prev, creditAccountId: '' }));
    }
  };
  
  // التحقق من صحة البيانات
  // وظيفة مساعدة للتحقق من وجود اتصال بالخادم
  const checkServerConnection = async () => {
    try {
      // محاولة الاتصال بالخادم للتحقق من الاتصال - استخدم نقطة نهاية أكثر موثوقية
      // استخدام نقطة نهاية أبسط على الخادم (نقطة نهاية الحسابات التي تعمل بالفعل)
      const { apiWithContext } = await import('../../../services/api');
      await apiWithContext.get('/finance/accounts/selection/active', 'receipt-voucher', { timeout: 5000 });
      return true;
    } catch (error) {
      console.error('فشل الاتصال بالخادم:', error);
      // عرض تفاصيل الخطأ للتشخيص
      if (axios.isAxiosError(error)) {
        console.error('تفاصيل خطأ الاتصال:', {
          message: error.message,
          code: error.code,
          status: error.response?.status,
          statusText: error.response?.statusText
        });
      }
      return false;
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.receiptNumber) {
      newErrors.receiptNumber = 'رقم السند مطلوب';
    }
    
    if (!formData.date) {
      newErrors.date = 'تاريخ السند مطلوب';
    }
    
    if (!formData.debitAccountId) {
      newErrors.debitAccountId = 'الحساب المدين مطلوب';
    }
    
    if (!formData.creditAccountId) {
      newErrors.creditAccountId = 'الحساب الدائن مطلوب';
    }
    
    if (formData.debitAccountId === formData.creditAccountId) {
      newErrors.creditAccountId = 'الحساب الدائن يجب أن يكون مختلفًا عن الحساب المدين';
    }
    
    if (!formData.amount || formData.amount <= 0) {
      newErrors.amount = 'المبلغ يجب أن يكون أكبر من صفر';
    }
    
    // البيان أصبح اختيارياً - تم إزالة التحقق من الإلزامية
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // معالجة حفظ النموذج
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setSaveLoading(true);
    
    try {
      // التحقق من تسجيل الدخول قبل إرسال البيانات
      if (!apiService.isAuthenticated()) {
        // إعادة توجيه إلى صفحة تسجيل الدخول إذا لم يكن المستخدم مسجلاً
        localStorage.setItem('redirect_after_login', '/finance/receipts/new');
        navigate('/login');
        return;
      }
      
      // إرسال البيانات إلى API
      const endpoint = isEditMode
        ? `/finance/receipts/${id}`
        : '/finance/receipts';
      
      const method = isEditMode ? 'put' : 'post';
      
      // التحقق من وجود اتصال بالخادم قبل إرسال البيانات
      const isConnected = await checkServerConnection();
      if (!isConnected) {
        setErrors(prev => ({
          ...prev,
          submit: 'تعذر الاتصال بالخادم. يرجى التحقق من تشغيل الخادم على المنفذ 5050 والتأكد من اتصال الشبكة'
        }));
        setSaveLoading(false);
        
        // إضافة نصائح إضافية لاستكشاف الأخطاء وإصلاحها
        console.info('نصائح لاستكشاف الأخطاء وإصلاحها:');
        console.info('1. تأكد من تشغيل الخادم الخلفي (على المنفذ 5050)');
        console.info('2. تأكد من أن عنوان API صحيح في ملف التكوين');
        console.info('3. افحص وحدة تحكم المتصفح للحصول على تفاصيل الخطأ');
        return;
      }

      // تحويل أسماء الحقول لتتوافق مع متطلبات الخادم
      // ملاحظة: حقل createdBy سيتم تعبئته تلقائياً على جانب الخادم من رمز المصادقة
      const today = new Date();
      const yearMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
      
      // استخدام رقم السند من بيانات النموذج (الآن مطلوب دائماً)
      // ملاحظة: الخادم قد يقوم بتعديل هذا الرقم في حالة السندات الجديدة
      
      // التحقق من وجود الفرع النشط
      if (!activeBranch) {
        setErrors({ general: 'يجب تحديد فرع نشط قبل حفظ سند القبض' });
        setSaveLoading(false);
        return;
      }

      // Get branch timezone
      const branchTimezone = activeBranch?.timezone || 'Asia/Riyadh';
      
      // Convert date to branch timezone with current time
      let dateToSend: string;
      if (formData.date) {
        const [year, month, day] = formData.date.split('-').map(Number);
        const now = moment.tz(branchTimezone);
        const dateWithTime = moment.tz({
          year,
          month: month - 1,
          day,
          hour: now.hour(),
          minute: now.minute(),
          second: now.second()
        }, branchTimezone);
        dateToSend = dateWithTime.format('YYYY-MM-DDTHH:mm:ss.SSSZ');
      } else {
        dateToSend = moment.tz(branchTimezone).format('YYYY-MM-DDTHH:mm:ss.SSSZ');
      }

      // تبسيط البنية لتتوافق مع نموذج Mongoose في الخادم
      const receipt = {
        receiptNumber: formData.receiptNumber,
        date: dateToSend,
        // استخدام معرفات الحسابات فقط
        debitAccount: formData.debitAccountId,
        creditAccount: formData.creditAccountId,
        amount: Number(formData.amount),
        description: formData.description,
        status: formData.status || 'تحت الموافقة',
        branchId: activeBranch._id,
        branchName: activeBranch.name
      };
      
      // طباعة بيانات التشخيص المفصلة
      // Add detailed validation and logging
      console.log('Final receipt data being sent:', JSON.stringify(receipt, null, 2));
      console.log('Validation before sending:', {
        hasReceiptNumber: !!receipt.receiptNumber,
        receiptNumberFormat: receipt.receiptNumber,
        debitAccountProvided: !!receipt.debitAccount,
        creditAccountProvided: !!receipt.creditAccount,
        amountIsNumber: typeof receipt.amount === 'number',
        amountIsPositive: receipt.amount > 0,
        dateFormat: formData.date
      });
      
      console.log('Sending data to server:', JSON.stringify(receipt, null, 2));
      
      try {
        // استخدام apiService لإرسال البيانات
        const response = await apiService[method](endpoint, receipt);
        console.log('API Response:', response);
      } catch (innerError) {
        console.error('Detailed error in request:', innerError);
        if (axios.isAxiosError(innerError) && innerError.response) {
          console.error('Server response data:', innerError.response.data);
          console.error('Server response status:', innerError.response.status);
          console.error('Server response headers:', innerError.response.headers);
          
          // Throw the error to be caught by the outer try/catch
          throw innerError;
        }
        throw innerError;
      }
      
      setSaveLoading(false);
      setSuccess(true);
      
      // إضافة علامة لتحديث القائمة عند العودة إليها
      localStorage.setItem('receipts_refresh_needed', 'true');
      // إضافة رسالة مخصصة
      localStorage.setItem('receipts_refresh_message',
        isEditMode ? 'تم تعديل سند القبض بنجاح' : 'تم إضافة سند قبض جديد بنجاح');
      
      // العودة إلى صفحة القائمة بعد النجاح
      setTimeout(() => {
        navigate('/finance/receipts');
      }, 1500);
      
    } catch (error: any) {
      console.error('خطأ في حفظ سند القبض:', error);
      
      // تحسين تسجيل تفاصيل الخطأ
      if (axios.isAxiosError(error)) {
        const axiosError = error;
        console.error('تفاصيل خطأ API:', {
          status: axiosError.response?.status,
          statusText: axiosError.response?.statusText,
          data: axiosError.response?.data,
          message: axiosError.message,
          code: axiosError.code,
          url: axiosError.config?.url
        });
        
        // تحديد نوع الخطأ وتقديم رسائل أكثر فائدة للمستخدم
        if (axiosError.code === 'ECONNABORTED') {
          setErrors(prev => ({
            ...prev,
            submit: 'انتهت مهلة الاتصال بالخادم. يرجى التحقق من سرعة الاتصال ومحاولة مرة أخرى.'
          }));
        } else if (axiosError.code === 'ERR_NETWORK') {
          setErrors(prev => ({
            ...prev,
            submit: 'تعذر الاتصال بالخادم. يرجى التحقق من تشغيل الخادم الخلفي (المنفذ 5050) واتصال الشبكة.'
          }));
        } else if (axiosError.response) {
          // خطأ مع استجابة من الخادم
          const responseData = axiosError.response.data;
          
          // التعامل مع أخطاء التحقق من صحة البيانات (Mongoose validation errors)
          if (responseData.name === 'ValidationError' && responseData.errors) {
            // عرض أخطاء التحقق في الحقول المناسبة
            const validationErrors: Record<string, string> = {};
            
            // استخراج رسائل الخطأ من كائن الأخطاء
            Object.keys(responseData.errors).forEach(field => {
              let fieldName = field;
              
              // تعيين أسماء الحقول إلى الاسم المستخدم في النموذج
              if (field === 'debitAccount') fieldName = 'debitAccountId';
              if (field === 'creditAccount') fieldName = 'creditAccountId';
              
              validationErrors[fieldName] = responseData.errors[field].message ||
                `حقل ${field} مطلوب أو غير صالح`;
            });
            
            // تحديث أخطاء النموذج
            setErrors(prev => ({
              ...prev,
              ...validationErrors,
              submit: 'يرجى تصحيح أخطاء التحقق من صحة البيانات المذكورة أعلاه'
            }));
          } else {
            // أخطاء أخرى من الخادم
            const errorMessage = responseData.message || 'حدث خطأ أثناء حفظ سند القبض';
            setErrors(prev => ({
              ...prev,
              submit: `${errorMessage} (رمز الخطأ: ${axiosError.response?.status})`
            }));
          }
        } else {
          setErrors(prev => ({
            ...prev,
            submit: 'حدث خطأ غير متوقع أثناء الاتصال بالخادم. يرجى المحاولة مرة أخرى.'
          }));
        }
      } else {
        // أخطاء غير أخطاء Axios
        setErrors(prev => ({
          ...prev,
          submit: 'حدث خطأ غير متوقع: ' + (error.message || 'خطأ غير معروف')
        }));
      }
      
      setSaveLoading(false);
    }
  };
  
  // العنوان بناءً على وضع النموذج
  const title = isEditMode ? 'تعديل سند قبض' : 'إضافة سند قبض جديد';

  // الحصول على الحساب بواسطة المعرّف
  const getAccountById = (id: string) => {
    if (!id) return null;
    
    const foundAccount = accounts.find(account => account.id === id);
    if (foundAccount) return foundAccount;
    
    console.warn(`لم يتم العثور على الحساب بالمعرف: ${id} في قائمة الحسابات المتاحة`);
    return null;
  };
  
  return (
    <Box sx={{ mb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
          border: '1px solid',
          borderColor: alpha('#000', 0.08),
          background: 'white'
        }}
      >
        {/* العنوان ومسار التنقل */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', mb: 1 }}>
              <PaidIcon sx={{ mr: 1, color: '#30c3b0' }} />
              {title}
            </Typography>
            
            <Breadcrumbs separator="›" aria-label="breadcrumb">
              <Link to="/finance/receipts" style={{ textDecoration: 'none', color: theme.palette.primary.main }}>
                سندات القبض
              </Link>
              <Typography color="text.primary">{title}</Typography>
            </Breadcrumbs>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ServerStatus onCheckConnection={checkServerConnection} />
            
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              component={Link}
              to="/finance/receipts"
              sx={{
                borderColor: '#30c3b0',
                color: '#30c3b0',
                '&:hover': { borderColor: '#25a899', backgroundColor: alpha('#30c3b0', 0.04) },
              }}
            >
              العودة للقائمة
            </Button>
          </Box>
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        {/* عرض التحميل أثناء جلب البيانات */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={40} sx={{ color: '#30c3b0' }} />
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* قسم اختيار القالب - يظهر فقط في وضع الإنشاء */}
            {!isEditMode && (
              <Box sx={{ mb: 3, p: 2, bgcolor: alpha('#30c3b0', 0.05), borderRadius: 2, border: '1px dashed', borderColor: '#30c3b0' }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', color: '#30c3b0' }}>
                  <BookmarkBorderIcon sx={{ mr: 1 }} />
                  استخدام قالب محفوظ (اختياري)
                </Typography>
                
                <Autocomplete
                  options={templates}
                  getOptionLabel={(option) => option.name}
                  value={selectedTemplate}
                  onChange={(_, value) => handleTemplateSelect(value)}
                  loading={templatesLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="اختر قالب محفوظ لملء الحسابات تلقائياً"
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {templatesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {option.name}
                        </Typography>
                        {option.description && (
                          <Typography variant="caption" color="text.secondary">
                            {option.description}
                          </Typography>
                        )}
                        <Typography variant="caption" display="block" color="text.secondary">
                          المدين: {option.debitAccount?.name} | الدائن: {option.creditAccount?.name}
                        </Typography>
                      </Box>
                    </li>
                  )}
                />
                
                {selectedTemplate && (
                  <Alert severity="info" sx={{ mt: 2 }} icon={<BookmarkBorderIcon />}>
                    تم تطبيق القالب "{selectedTemplate.name}" - الحسابات المدينة والدائنة تم ملؤها تلقائياً
                  </Alert>
                )}
              </Box>
            )}
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                    معلومات السند الأساسية
                  </Typography>
                  
                  <TextField
                    name="receiptNumber"
                    label="رقم السند"
                    value={formData.receiptNumber}
                    onChange={handleInputChange}
                    fullWidth
                    margin="normal"
                    required
                    error={!!errors.receiptNumber}
                    helperText={errors.receiptNumber || (isEditMode ? "" : "سيتم تأكيد الرقم النهائي بعد الحفظ")}
                    InputProps={{
                      startAdornment: (
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                          #
                        </Typography>
                      ),
                    }}
                  />
                  
                  <TextField
                    name="date"
                    label="تاريخ السند"
                    type="date"
                    value={formData.date}
                    onChange={handleDateChange}
                    error={!!errors.date}
                    helperText={errors.date}
                    fullWidth
                    margin="normal"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  
                  {isEditMode && (
                    <FormControl fullWidth margin="normal">
                      <InputLabel>حالة السند</InputLabel>
                      <Select
                        value={formData.status}
                        label="حالة السند"
                        disabled
                      >
                        <MenuItem value="مؤكد">مؤكد</MenuItem>
                        <MenuItem value="تحت الموافقة">تحت الموافقة</MenuItem>
                        <MenuItem value="مرفوض">مرفوض</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                    التفاصيل المالية
                  </Typography>
                  
                  <Autocomplete
                    key={`debit-account-${formData.debitAccountId}`}
                    options={accounts}
                    getOptionDisabled={(option) => option.type === 'رئيسي'}
                    getOptionLabel={(option) => option.label || `${option.accountNumber} - ${option.name}`}
                    value={formData.debitAccountId ? getAccountById(formData.debitAccountId) : null}
                    onChange={handleDebitAccountChange}
                    loading={accountsLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="الحساب المدين (يتم إضافة المبلغ إليه)"
                        margin="normal"
                        error={!!errors.debitAccountId}
                        helperText={errors.debitAccountId || "اختر الحساب المدين"}
                        required
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {accountsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => {
                      return option.id === (value?.id || formData.debitAccountId);
                    }}
                  />
                  
                  <Autocomplete
                    key={`credit-account-${formData.creditAccountId}`}
                    options={accounts}
                    getOptionDisabled={(option) => option.type === 'رئيسي'}
                    getOptionLabel={(option) => option.label || `${option.accountNumber} - ${option.name}`}
                    value={formData.creditAccountId ? getAccountById(formData.creditAccountId) : null}
                    onChange={handleCreditAccountChange}
                    loading={accountsLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="الحساب الدائن (مصدر الإيراد)"
                        margin="normal"
                        error={!!errors.creditAccountId}
                        helperText={errors.creditAccountId || "اختر الحساب الدائن"}
                        required
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {accountsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => {
                      return option.id === (value?.id || formData.creditAccountId);
                    }}
                  />
                  
                  <TextField
                    name="amount"
                    label="المبلغ"
                    value={formData.amount}
                    onChange={handleInputChange}
                    type="number"
                    InputProps={{
                      endAdornment: <Typography sx={{ ml: 1 }}>ريال</Typography>,
                    }}
                    error={!!errors.amount}
                    helperText={errors.amount}
                    fullWidth
                    margin="normal"
                    required
                  />
                  
                  {/* عرض قيمة المبلغ للتصحيح */}
                  {isEditMode && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                      قيمة المبلغ في النظام: {formData.amount} ريال
                    </Typography>
                  )}
                </Box>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  name="description"
                  label="البيان (اختياري)"
                  value={formData.description}
                  onChange={handleInputChange}
                  error={!!errors.description}
                  helperText={errors.description}
                  fullWidth
                  multiline
                  rows={3}
                  margin="normal"
                />
              </Grid>
            </Grid>
            
            <Divider sx={{ my: 3 }} />
            
            {/* رسالة النجاح */}
            {success && (
              <Alert
                severity="success"
                icon={<CheckCircleOutlineIcon fontSize="inherit" />}
                sx={{ mb: 3 }}
              >
                تم {isEditMode ? 'تعديل' : 'إضافة'} سند القبض بنجاح
              </Alert>
            )}
            
            {/* رسالة الخطأ */}
            {errors.submit && (
              <Alert
                severity="error"
                sx={{ mb: 3 }}
              >
                {errors.submit}
              </Alert>
            )}
            
            {/* أزرار الإجراءات */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/finance/receipts')}
                disabled={saveLoading}
              >
                إلغاء
              </Button>
              
              <Button
                type="submit"
                variant="contained"
                startIcon={isEditMode ? <SaveIcon /> : <AddIcon />}
                disabled={saveLoading || success}
                sx={{
                  bgcolor: '#30c3b0',
                  '&:hover': { bgcolor: '#25a899' },
                  fontWeight: 600,
                  px: 3,
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(48, 195, 176, 0.2)',
                }}
              >
                {saveLoading ? (
                  <CircularProgress size={24} sx={{ color: 'white' }} />
                ) : (
                  isEditMode ? 'حفظ التغييرات' : 'إضافة السند'
                )}
              </Button>
            </Box>
          </form>
        )}
      </Paper>
    </Box>
  );
};

export default ReceiptForm;